import type { PermissionDetailItem } from "./api/user-permissions-detail";
import {
  canonicalPermissionName,
  dedupePermissionCatalog,
} from "./permission-name-canonical";

export type PermissionCatalogItem = {
  id: string;
  name: string;
  description?: string | null;
  resource?: string | null;
  action?: string | null;
};

export type PermissionActionView = {
  permissionId: string;
  permissionName: string;
  actionKey: string;
  label: string;
  description?: string | null;
  enabled: boolean;
  switchDisabled: boolean;
  disabledReason?: string;
  source?: "role" | "direct";
  roleName?: string;
};

export type ResourcePermissionGroup = {
  resourceKey: string;
  resourceLabel: string;
  actions: PermissionActionView[];
};

export function formatResourceLabel(resource: string): string {
  return resource
    .replace(/-/g, " ")
    .replace(/:/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatActionLabel(action: string): string {
  return action
    .replace(/-/g, " ")
    .replace(/:/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Legacy Smart dash names: `{action}-{resource}` (e.g. create-product, basic-read-product-settings). */
const LEGACY_SMART_ACTION_PREFIXES = [
  "advanced-write",
  "advanced-read",
  "intermediate-write",
  "intermediate-read",
  "basic-write",
  "basic-read",
  "update-own",
  "create",
  "read",
  "update",
  "delete",
  "use",
  "open",
  "disguise",
  "debug",
  "manage",
] as const;

function parseLegacyDashPermission(
  name: string,
): { resource: string; action: string } | null {
  if (!name.includes("-") || name.includes(":")) return null;

  for (const prefix of LEGACY_SMART_ACTION_PREFIXES) {
    const marker = `${prefix}-`;
    if (name.startsWith(marker) && name.length > marker.length) {
      return { action: prefix, resource: name.slice(marker.length) };
    }
  }

  return null;
}

function parsePermissionPartsFromName(
  name: string,
): { resource: string; action: string } | null {
  if (name.includes(":")) {
    const parts = name.split(":").filter(Boolean);
    if (parts.length >= 3) {
      return {
        resource: parts.slice(0, -1).join(":"),
        action: parts[parts.length - 1]!,
      };
    }
    if (parts.length >= 2) {
      return {
        resource: parts[0] ?? "general",
        action: parts[1] ?? "access",
      };
    }
  }

  const legacy = parseLegacyDashPermission(name);
  if (legacy) return legacy;

  const dash = name.indexOf("-");
  if (dash > 0) {
    return {
      resource: name.slice(dash + 1),
      action: name.slice(0, dash),
    };
  }

  return null;
}

export function parsePermissionParts(
  name: string,
  resource?: string | null,
  action?: string | null,
): { resource: string; action: string } {
  const fromName = parsePermissionPartsFromName(name);
  if (fromName) return fromName;

  if (resource?.trim() && action?.trim()) {
    return { resource: resource.trim(), action: action.trim() };
  }

  return { resource: "general", action: name };
}

export function formatRoleLabel(role: string): string {
  const stripped = role.replace(/^pro:/, "");
  return stripped
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function viaRoleTooltip(roleName: string | undefined): string | undefined {
  if (!roleName) return "Via role";
  return `Via ${formatRoleLabel(roleName)}`;
}

function buildActionView(
  item: PermissionDetailItem,
  options?: { forceReadOnly?: boolean },
): PermissionActionView {
  const { resource, action } = parsePermissionParts(
    item.name,
    item.resource,
    item.action,
  );
  const fromRole = item.source === "role";
  const readOnly = options?.forceReadOnly ?? fromRole;
  return {
    permissionId: item.id,
    permissionName: item.name,
    actionKey: action,
    label: formatActionLabel(action),
    description: item.description ?? undefined,
    enabled: true,
    switchDisabled: readOnly,
    disabledReason: fromRole
      ? viaRoleTooltip(item.roleName)
      : readOnly
        ? "Read-only"
        : undefined,
    source: item.source,
    roleName: item.roleName,
  };
}

function mergeActionViews(
  existing: PermissionActionView | undefined,
  incoming: PermissionActionView,
): PermissionActionView {
  if (!existing) return incoming;
  if (existing.switchDisabled && !incoming.switchDisabled) {
    return incoming;
  }
  if (!existing.switchDisabled && incoming.switchDisabled) {
    return existing;
  }
  return existing;
}

export function groupPermissionItems(
  items: PermissionDetailItem[],
  options?: { readOnly?: boolean },
): ResourcePermissionGroup[] {
  const byResource = new Map<string, Map<string, PermissionActionView>>();

  for (const item of items) {
    const view = buildActionView(item, { forceReadOnly: options?.readOnly });
    const { resource, action } = parsePermissionParts(
      item.name,
      item.resource,
      item.action,
    );
    const resourceMap =
      byResource.get(resource) ?? new Map<string, PermissionActionView>();
    resourceMap.set(action, mergeActionViews(resourceMap.get(action), view));
    byResource.set(resource, resourceMap);
  }

  return [...byResource.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([resourceKey, actionsMap]) => ({
      resourceKey,
      resourceLabel: formatResourceLabel(resourceKey),
      actions: [...actionsMap.values()].sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    }));
}

/**
 * Build resource groups for Lion Pro direct grants: full catalog with effective state.
 */
export function buildDirectPermissionGroups(
  catalog: PermissionCatalogItem[],
  directIds: Set<string>,
  effectiveByName: Map<string, PermissionDetailItem>,
): ResourcePermissionGroup[] {
  const dedupedCatalog = dedupePermissionCatalog(catalog);
  const catalogById = new Map(dedupedCatalog.map((p) => [p.id, p]));
  const directCanonical = new Set<string>();
  for (const id of directIds) {
    const perm = catalogById.get(id);
    if (perm) directCanonical.add(canonicalPermissionName(perm.name));
  }

  const byResource = new Map<string, PermissionActionView[]>();

  for (const perm of dedupedCatalog) {
    const { resource, action } = parsePermissionParts(
      perm.name,
      perm.resource,
      perm.action,
    );
    const canonical = canonicalPermissionName(perm.name);
    const effective =
      effectiveByName.get(perm.name) ?? effectiveByName.get(canonical);
    const fromRole = effective?.source === "role";
    const hasDirect =
      directIds.has(perm.id) || directCanonical.has(canonical);
    const enabled = Boolean(effective) || hasDirect;

    const view: PermissionActionView = {
      permissionId: perm.id,
      permissionName: perm.name,
      actionKey: action,
      label: formatActionLabel(action),
      description: perm.description ?? undefined,
      enabled,
      switchDisabled: fromRole && !hasDirect,
      disabledReason:
        fromRole && !hasDirect
          ? viaRoleTooltip(effective?.roleName)
          : undefined,
      source: hasDirect ? "direct" : effective?.source,
      roleName: effective?.roleName,
    };

    const list = byResource.get(resource) ?? [];
    list.push(view);
    byResource.set(resource, list);
  }

  return [...byResource.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([resourceKey, actions]) => ({
      resourceKey,
      resourceLabel: formatResourceLabel(resourceKey),
      actions: actions.sort((a, b) => a.label.localeCompare(b.label)),
    }));
}

/**
 * Read-only resource groups for Lion Smart: full catalog with effective grants
 * merged in, no role sub-grouping.
 */
export function buildReadOnlyCatalogPermissionGroups(
  catalog: PermissionCatalogItem[],
  effectiveItems: PermissionDetailItem[],
): ResourcePermissionGroup[] {
  const effectiveByName = collectEffectiveByName(effectiveItems);
  const catalogNames = new Set<string>();
  const byResource = new Map<string, Map<string, PermissionActionView>>();

  const addView = (
    view: PermissionActionView,
    resource: string,
    action: string,
  ) => {
    const resourceMap =
      byResource.get(resource) ?? new Map<string, PermissionActionView>();
    resourceMap.set(action, mergeActionViews(resourceMap.get(action), view));
    byResource.set(resource, resourceMap);
  };

  for (const perm of catalog) {
    catalogNames.add(perm.name);
    const { resource, action } = parsePermissionParts(
      perm.name,
      perm.resource,
      perm.action,
    );
    const effective = effectiveByName.get(perm.name);
    addView(
      {
        permissionId: perm.id,
        permissionName: perm.name,
        actionKey: action,
        label: formatActionLabel(action),
        description: perm.description ?? undefined,
        enabled: Boolean(effective),
        switchDisabled: true,
        disabledReason: effective
          ? viaRoleTooltip(effective.roleName)
          : undefined,
        source: effective?.source,
        roleName: effective?.roleName,
      },
      resource,
      action,
    );
  }

  for (const item of effectiveItems) {
    if (catalogNames.has(item.name)) continue;
    const view = buildActionView(item, { forceReadOnly: true });
    const { resource, action } = parsePermissionParts(
      item.name,
      item.resource,
      item.action,
    );
    addView(view, resource, action);
  }

  return [...byResource.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([resourceKey, actionsMap]) => ({
      resourceKey,
      resourceLabel: formatResourceLabel(resourceKey),
      actions: [...actionsMap.values()].sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    }));
}

export function collectEffectiveByName(
  items: PermissionDetailItem[],
): Map<string, PermissionDetailItem> {
  const map = new Map<string, PermissionDetailItem>();
  for (const item of items) {
    const key = canonicalPermissionName(item.name);
    const existing = map.get(key);
    if (!existing || (existing.source === "role" && item.source === "direct")) {
      map.set(key, item);
    }
  }
  return map;
}
